import type { PeriodVM } from '~/composables/useRequest/requests/data-contracts';


const CACHE_KEY_PERIODS = 'account-periods-list';
const CACHE_KEY_ACCOUNT_ID = 'account-periods-accountId';
const CACHE_KEY_LOADING = 'account-periods-loading';
const CACHE_KEY_PROMISE = 'account-periods-loadPromise';
const CACHE_KEY_LOADED_AT = 'account-periods-loadedAt';
const CACHE_KEY_TTL_TIMER = 'account-periods-ttlTimer';
const CACHE_TTL_MS = 60_000;

/**
 * Общий кэш available-period для аккаунта.
 * ensureLoaded всегда возвращает копию — clear() не затрагивает данные в компонентах.
 */
export default function accountPeriodsData() {
  const { account } = useRequest();

  const periods = useState<PeriodVM[]>(CACHE_KEY_PERIODS, () => []);
  const cachedAccountId = useState<number | null>(CACHE_KEY_ACCOUNT_ID, () => null);
  const isLoading = useState<boolean>(CACHE_KEY_LOADING, () => false);
  const loadPromise = useState<Promise<PeriodVM[]> | null>(CACHE_KEY_PROMISE, () => null);
  const loadedAt = useState<number | null>(CACHE_KEY_LOADED_AT, () => null);
  const ttlTimer = useState<ReturnType<typeof setTimeout> | null>(CACHE_KEY_TTL_TIMER, () => null);

  const clone = (list: PeriodVM[]) => list.slice();

  const clearTtlTimer = () => {
    if (ttlTimer.value != null) {
      clearTimeout(ttlTimer.value);
      ttlTimer.value = null;
    }
  };

  const clear = () => {
    clearTtlTimer();
    periods.value = [];
    cachedAccountId.value = null;
    loadedAt.value = null;
    loadPromise.value = null;
    isLoading.value = false;
  };

  const scheduleClear = () => {
    clearTtlTimer();

    ttlTimer.value = setTimeout(() => {
      clear();
    }, CACHE_TTL_MS);
  };

  const isFresh = (accountId: number) =>
    cachedAccountId.value === accountId &&
    loadedAt.value != null &&
    Date.now() - loadedAt.value < CACHE_TTL_MS;

  const ensureLoaded = (accountId: number, mode: 'soft' | 'hard' = 'soft'): Promise<PeriodVM[]> => {
    if (mode === 'soft' && loadPromise.value && cachedAccountId.value === accountId) {
      if (isLoading.value || isFresh(accountId)) {
        return loadPromise.value.then(clone);
      }

      clear();
    }

    if (mode === 'hard' || cachedAccountId.value !== accountId) {
      clear();
    }

    cachedAccountId.value = accountId;

    const promise = (async () => {
      try {
        isLoading.value = true;

        const { payload: { list } } = await account.accountPeriodControllerGetAccountPeriods(accountId, {
          limit: 100,
          page: 0,
        });

        if (cachedAccountId.value !== accountId) {
          return clone(list);
        }

        periods.value = clone(list);
        loadedAt.value = Date.now();
        scheduleClear();

        return clone(list);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch account periods:', error);

        // Do not cache failures — next soft call must retry
        if (cachedAccountId.value === accountId) {
          clear();
        }

        return [];
      } finally {
        if (cachedAccountId.value === accountId) {
          isLoading.value = false;
        }
      }
    })();

    loadPromise.value = promise;

    return promise.then(clone);
  };

  const getActivePeriodId = (list: PeriodVM[]): number | undefined => {
    const active = list.find(item => item.is_active);

    return active?.id ?? list[0]?.id;
  };

  return {
    isLoading,
    ensureLoaded,
    getActivePeriodId,
    clear,
  };
}
